/* Copyright (c) 2024-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import {
    addDays,
    addHours,
    addMilliseconds,
    addMinutes,
    addMonths,
    addSeconds,
    addWeeks,
    addYears,
    differenceInHours,
    differenceInMilliseconds,
    differenceInMinutes,
    differenceInSeconds,
    endOfDay,
    formatDistanceToNow,
    formatISO,
    getDay,
    getMonth,
    getYear,
    isAfter,
    isBefore,
    isEqual,
    isSameDay,
    isSameMonth,
    isSameWeek,
    isSameYear,
    isValid,
    parseISO,
    startOfDay,
    startOfMonth,
    subDays,
    subHours,
    subMilliseconds,
    subMinutes,
    subMonths,
    subSeconds,
    subWeeks,
    subYears,
    Locale,
    Duration
} from 'date-fns';
import { defaultLocale, locales } from './date-locales';
import { DateGranulity, hourToSeconds, minuteToSeconds, oneDayInSeconds, TimeUnit } from './date.constants';

export class DateTimeUtils {
    static parseISODate(dateStr: string): Date {
        return parseISO(dateStr);
    }

    static isValidDate(date: Date | string): boolean {
        try {
            if (typeof date === 'string') {
                date = this.parseISODate(date);
            }
            return isValid(date);
        } catch (error) {
            return false;
        }
    }

    static getISODate(date: Date): string {
        return formatISO(date);
    }

    /**
     * Calculates the difference between two dates in the specified metric.
     *
     * @param {Date} currentDate - The more recent date.
     * @param {Date} previousDate - The earlier date.
     * @param {string} metric - The metric in which to measure the difference (seconds, minutes, hours, milliseconds).
     * @returns {number} - The difference in the specified metric.
     */
    static getDifferenceInTime(currentDate: Date, previousDate: Date, metric: TimeUnit): number {
        switch (metric) {
            case TimeUnit.SECONDS:
                return differenceInSeconds(currentDate, previousDate);
            case TimeUnit.MINUTES:
                return differenceInMinutes(currentDate, previousDate);
            case TimeUnit.HOURS:
                return differenceInHours(currentDate, previousDate);
            case TimeUnit.MILLISECONDS:
                return differenceInMilliseconds(currentDate, previousDate);
            default:
                throw new Error(`Unsupported metric: ${metric}`);
        }
    }

    /**
     * Adds time to provided date.
     *
     * @param {Date} date - The date to add time to.
     * @param {number} time - Units of time to add.
     * @param {string} metric - The metric in which time is to be added (seconds, minutes, hours, milliseconds, days, months, years, weeks).
     * @returns {Date} - Updated date.
     */
    static addTime(date: Date, time: number, metric: TimeUnit): Date {
        switch (metric) {
            case TimeUnit.HOURS:
                return addHours(date, time);
            case TimeUnit.MINUTES:
                return addMinutes(date, time);
            case TimeUnit.SECONDS:
                return addSeconds(date, time);
            case TimeUnit.MILLISECONDS:
                return addMilliseconds(date, time);
            case TimeUnit.DAYS:
                return addDays(date, time);
            case TimeUnit.WEEKS:
                return addWeeks(date, time);
            case TimeUnit.MONTHS:
                return addMonths(date, time);
            case TimeUnit.YEARS:
                return addYears(date, time);
            default:
                throw new Error(
                    'Invalid metric. Use "hours", "minutes", "seconds", "milliseconds", "days", "weeks", "months", or "years".'
                );
        }
    }

    /**
     * Subtracts time from provided date.
     *
     * @param {Date} date - The date to subtract time from.
     * @param {number} time - Units of time to subtract.
     * @param {string} metric - The metric in which time is to be subtracted (seconds, minutes, hours, milliseconds, days, months, years, weeks).
     * @returns {Date} - Updated Date.
     */
    static subtractTime(date: Date, time: number, metric: TimeUnit): Date {
        switch (metric) {
            case TimeUnit.HOURS:
                return subHours(date, time);
            case TimeUnit.MINUTES:
                return subMinutes(date, time);
            case TimeUnit.SECONDS:
                return subSeconds(date, time);
            case TimeUnit.MILLISECONDS:
                return subMilliseconds(date, time);
            case TimeUnit.DAYS:
                return subDays(date, time);
            case TimeUnit.WEEKS:
                return subWeeks(date, time);
            case TimeUnit.MONTHS:
                return subMonths(date, time);
            case TimeUnit.YEARS:
                return subYears(date, time);
            default:
                throw new Error(
                    'Invalid metric. Use "hours", "minutes", "seconds", "milliseconds", "days", "weeks", "months", or "years".'
                );
        }
    }

    static getStartOfDay(date: Date): Date {
        return startOfDay(date);
    }

    static getEndOfDay(date: Date): Date {
        return endOfDay(date);
    }

    static getDayOfWeek(date: Date): number {
        return getDay(date);
    }

    static getStartOfMonth(date: Date): Date {
        return startOfMonth(date);
    }

    static getMonthOfYear(date: Date): number {
        return getMonth(date) + 1;
    }

    static getYear(date: Date): number {
        return getYear(date);
    }

    static isSameOrAfter(date1: Date, date2: Date, granulity: DateGranulity): boolean {
        switch (granulity) {
            case DateGranulity.DAY:
                return this.isSameOrAfterDay(date1, date2);
            case DateGranulity.WEEK:
                return this.isSameOrAfterWeek(date1, date2);
            case DateGranulity.MONTH:
                return this.isSameOrAfterMonth(date1, date2);
            case DateGranulity.YEAR:
                return this.isSameOrAfterYear(date1, date2);
        }
    }

    static isSame(date1: Date, date2: Date, granulity: DateGranulity): boolean {
        switch (granulity) {
            case DateGranulity.DAY:
                return isSameDay(date1, date2);
            case DateGranulity.WEEK:
                return isSameWeek(date1, date2);
            case DateGranulity.MONTH:
                return isSameMonth(date1, date2);
            case DateGranulity.YEAR:
                return isSameYear(date1, date2);
        }
    }

    static createDuration(totalSeconds): Duration {
        const hours = Math.floor(totalSeconds / hourToSeconds);
        const minutes = Math.floor((totalSeconds % hourToSeconds) / minuteToSeconds);
        const seconds = totalSeconds % minuteToSeconds;
        return {
            seconds: seconds,
            minutes: minutes,
            hours: hours
        };
    }

    // converts time string to milliseconds
    static convertDurationToMilliseconds(time: string): number {
        const [hours, minutes, seconds] = time.split(':').map(Number);
        const durationInMilliseconds = (hours * hourToSeconds + minutes * minuteToSeconds + seconds) * 1000;
        return durationInMilliseconds;
    }

    static isSameOrAfterDay(date1: Date, date2: Date): boolean {
        return isEqual(date1, date2) || isAfter(date1, date2);
    }

    static isSameOrAfterWeek(date1: Date, date2: Date): boolean {
        return isSameWeek(date1, date2) || isAfter(date1, date2);
    }

    static isSameOrAfterMonth(date1: Date, date2: Date): boolean {
        return isSameMonth(date1, date2) || isAfter(date1, date2);
    }

    static isSameOrAfterYear(date1: Date, date2: Date): boolean {
        return isSameYear(date1, date2) || isAfter(date1, date2);
    }

    /**
     * Checks if a given date is between two other dates, with optional inclusivity.
     *
     * @param {Date} date - The date to check.
     * @param {Date} startDate - The start date of the range.
     * @param {Date} endDate - The end date of the range.
     * @param {'()' | '[)' | '(]' | '[]'} [inclusivity='()'] - A string indicating whether the start/end dates are inclusive.
     *      - '()'  : exclusive of both start and end dates.
     *      - '[)'  : inclusive of start date, exclusive of end date.
     *      - '(]'  : exclusive of start date, inclusive of end date.
     *      - '[]'  : inclusive of both start and end dates.
     * @returns {boolean} - Returns true if the date is within the range according to the specified inclusivity, otherwise false.
     */
    static isBetween(
        date: Date,
        startDate: Date,
        endDate: Date,
        inclusivity: '()' | '[)' | '(]' | '[]' = '()'
    ): boolean {
        const isAfterStart = inclusivity[0] === '[' ? !isBefore(date, startDate) : isAfter(date, startDate);
        const isBeforeEnd = inclusivity[1] === ']' ? !isAfter(date, endDate) : isBefore(date, endDate);
        return isAfterStart && isBeforeEnd;
    }

    static getSecondsFromDate(date: Date): number {
        return date.getSeconds();
    }

    static getMinutesFromDate(date: Date): number {
        return date.getMinutes();
    }

    static getHoursFromDate(date: Date): number {
        return date.getHours();
    }

    // returns the number of days that have passed since a specific date to the present
    static getDaysElapsedFromDate(date: Date): number {
        const startDateTimeInMS = new Date(date).getTime();
        const currentTimeStamp = new Date().getTime();
        const elapsedTimeInSeconds = Math.max(0, Math.floor((currentTimeStamp - startDateTimeInMS) / 1000));
        return Math.max(0, Math.floor(elapsedTimeInSeconds / oneDayInSeconds));
    }

    static secondsToHHMMSS(seconds: number): string {
        const hours = Math.floor(seconds / hourToSeconds);
        const minutes = Math.floor((seconds % hourToSeconds) / minuteToSeconds);
        const remainingSeconds = Math.floor(seconds % 60);

        const formattedHours = String(hours).padStart(2, '0');
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(remainingSeconds).padStart(2, '0');
        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }

    static convertTimeStringToSeconds(timeString: string): number {
        const [hours, minutes, seconds] = timeString.split(':').map(Number);
        const totalSeconds = hours * hourToSeconds + minutes * minuteToSeconds + seconds;
        return totalSeconds;
    }

    static fromNow(date: Date | string, localeCode?: string, options?: {}): string {
        try {
            if (typeof date === 'string') {
                date = this.parseISODate(date);
            }
            if (localeCode) {
                const detectedLocale = this.detectLocale(localeCode);
                return formatDistanceToNow(date, { ...options, locale: detectedLocale });
            }
            return formatDistanceToNow(date, { ...options });
        } catch (error) {
            return '';
        }
    }

    static detectLocale(localeCode: string): Locale {
        return locales[localeCode] || defaultLocale;
    }
}
